import React, { useState, useMemo } from "react";
import useReveal from "../hooks/useReveal";
import PageHero from "../components/PageHero";
import CtaPremium from "../components/CtaPremium";
import { PROJECTS, PROJECT_CATEGORIES } from "../mock";
import { ArrowUpRight, X, ChevronDown, SlidersHorizontal, Check } from "lucide-react";

export default function ProjectsPage() {
  useReveal();
  const [filter, setFilter] = useState("All");
  const [open, setOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const filtered = useMemo(() => filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === filter), [filter]);

  return (
    <div className="page-enter">
      <PageHero
        variant="light"
        eyebrow="Projects — selected work"
        title="Twenty-five years of"
        italic="UK's finest builds."
        subtitle="From Residential Renovations to Complete property improvements — a curated selection of projects we're proud to have delivered."
        image="https://images.unsplash.com/photo-1710883727426-0c9e50a5824a?crop=entropy&cs=srgb&fm=jpg&q=90&w=2400"
      />

      {/* Premium dropdown filter */}
      <section className="bg-bone py-8 border-y border-line sticky top-[78px] z-30 backdrop-blur-md" style={{ background: "rgba(245,241,234,0.92)" }}>
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 flex flex-wrap items-center gap-6">
          <div className="text-eyebrow text-ink/55">Filter by discipline</div>

          <div className="relative">
            <button
              onClick={() => setOpen(!open)}
              className="flex items-center gap-3 border border-ink bg-bone hover:bg-ink hover:text-bone transition-colors px-5 py-3 min-w-[280px] justify-between"
            >
              <div className="flex items-center gap-3">
                <SlidersHorizontal className="w-4 h-4" />
                <span className="font-display text-lg">{filter}</span>
              </div>
              <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
                <div className="absolute left-0 top-full mt-2 w-[320px] bg-bone border border-ink shadow-2xl z-40 max-h-[60vh] overflow-y-auto">
                  {PROJECT_CATEGORIES.map((c) => {
                    const active = filter === c;
                    const count = c === "All" ? PROJECTS.length : PROJECTS.filter((p) => p.category === c).length;
                    return (
                      <button
                        key={c}
                        onClick={() => { setFilter(c); setOpen(false); }}
                        className={`w-full flex items-center justify-between gap-4 px-5 py-3.5 text-left transition-colors border-b border-line last:border-b-0 ${
                          active ? "bg-ink text-bone" : "hover:bg-bone-2 text-ink"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {active && <Check className="w-4 h-4 text-amber" />}
                          <span className={`font-display text-lg ${active ? "" : "ml-7"}`}>{c}</span>
                        </div>
                        <span className={`text-eyebrow ${active ? "text-amber" : "text-ink/45"}`}>{String(count).padStart(2, "0")}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <span className="ml-auto text-eyebrow text-ink/55 whitespace-nowrap">{filtered.length} project{filtered.length !== 1 ? "s" : ""} shown</span>
        </div>
      </section>

      {/* Grid — masonry-like with animated items */}
      <section className="bg-bone py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p, i) => {
              const spanTall = i % 5 === 0;
              return (
                <button
                  key={p.id}
                  onClick={() => setLightbox(p)}
                  className={`group relative overflow-hidden bg-ink text-left rv ${spanTall ? "lg:row-span-2" : ""}`}
                  style={{ transitionDelay: `${(i % 6) * 60}ms` }}
                >
                  <div className={`w-full overflow-hidden ${spanTall ? "aspect-[4/6]" : "aspect-[4/5]"}`}>
                    <img src={p.thumb} alt={p.title} className="img-zoom w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/30 to-transparent opacity-80 group-hover:opacity-95 transition-opacity" />
                  <div className="absolute top-5 left-5 text-eyebrow text-bone bg-black/35 backdrop-blur border border-bone/20 px-2.5 py-1">{p.category}</div>
                  <div className="absolute top-5 right-5 h-10 w-10 bg-transparent group-hover:bg-amber border border-bone/30 group-hover:border-amber flex items-center justify-center transition-all">
                    <ArrowUpRight className="w-4 h-4 text-bone group-hover:text-ink" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-bone">
                    <div className="text-eyebrow text-amber mb-1">{String(p.id).padStart(2, "0")}</div>
                    <h3 className="font-display text-2xl md:text-3xl leading-tight mb-1">{p.title}</h3>
                    <p className="text-sm text-bone/70">{p.scope}</p>
                    <p className="text-sm text-bone/55 mt-1">{p.location} · {p.year}</p>
                  </div>
                </button>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="py-20 text-center text-ink-soft">No projects in this category yet.</div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 bg-ink/95 z-50 flex items-center justify-center p-4 lg:p-10" onClick={() => setLightbox(null)}>
          <div className="relative max-w-6xl w-full bg-ink" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setLightbox(null)} className="absolute -top-12 right-0 text-bone hover:text-amber transition-colors" aria-label="close">
              <X className="w-8 h-8" />
            </button>
            <img src={lightbox.hero} alt={lightbox.title} className="w-full max-h-[70vh] object-cover" />
            <div className="p-8 lg:p-10 text-bone grid lg:grid-cols-12 gap-6">
              <div className="lg:col-span-8">
                <div className="text-eyebrow text-amber mb-3">{lightbox.category}</div>
                <h3 className="font-display text-3xl lg:text-5xl leading-tight">{lightbox.title}</h3>
              </div>
              <div className="lg:col-span-4 grid grid-cols-2 gap-6">
                <div><div className="text-eyebrow text-bone/50 mb-1">Location</div><div className="font-display text-xl">{lightbox.location}</div></div>
                <div><div className="text-eyebrow text-bone/50 mb-1">Year</div><div className="font-display text-xl">{lightbox.year}</div></div>
                <div className="col-span-2"><div className="text-eyebrow text-bone/50 mb-1">Scope</div><div className="text-bone">{lightbox.scope}</div></div>
              </div>
            </div>
          </div>
        </div>
      )}

      <CtaPremium />
    </div>
  );
}
